/**
 * KONFIGURACJA BIOMÓW (6 normalnych: Meadow t1, Mountain t2, Star Wars/Tatooine t3,
 * Pixel World t4, Ocean t5, Food/Fast Food t6 + 1 edukacyjny: Świat Flag Europa t0).
 *
 * Każdy biom to paczka contentu: mapa, gdzie się spawnują obiekty, jakie jajka
 * są w sklepie, jakie jest tło, ile waluty mnożymy.
 *
 * Layout Meadow:
 *   - Mapa 2000×1500 px (gracz nie wychodzi poza)
 *   - Gracz spawn: środek mapy (1000, 750)
 *   - 13 destructibles rozrzuconych po mapie (im dalej, tym lepszy reward)
 *   - Kryształ ("mini-boss") w przeciwległym rogu od spawn'a — wymusza eksplorację
 *   - 4 jajka dostępne w sklepie (egg-starter / basic / rare / legendary)
 *
 * Wszystkie pozycje są deterministyczne (`positions: [...]`), nie losowane.
 * Daje to przewidywalność dla testów i balansu. Random spawn dodamy w wyższych
 * biomach gdy będzie potrzebne.
 */

import { HALLOWEEN_BIOME } from './halloweenBiome.config';
import type { BiomeDef } from '@/domain/types/biome';
import { biomeUnlockCost, biomePowerRequirement } from './balance.config';

export const BIOMES: BiomeDef[] = [
  HALLOWEEN_BIOME,
  {
    id: 'biome-meadow',
    name: { pl: 'Łąka', en: 'Meadow' },
    tier: 1,

    unlock: {
      cost: { currency: 'coins', amount: biomeUnlockCost(1) }, // 0 — startowy
      // requiredPower / requiredBiomeId — brak, każdy gracz zaczyna tutaj
    },

    worldSize: { w: 2000, h: 1500 },
    playerSpawn: { x: 1000, y: 750 },

    scene: {
      backgroundTextureKey: 'bg_meadow_tile',
      backgroundColor: '#A8E6A1',          // pastelowa zieleń łąki — fallback i kolor pod teksturą
      bgmKey: 'bgm_meadow',                // TODO: BGM dodamy później
      ambientFx: undefined,
      atmosphere: 'meadow_sun',            // ciepłe god rays + złota winieta (słoneczne południe)
      parallax: 'meadow_clouds',           // cienie przepływających chmur (prototyp parallaxu — patrz parallax.config.ts)
      critters: 'meadow_critters',         // motyle / ptak / biedronka (prototyp stworzonek — patrz critters.config.ts)
    },

    destructibleSpawns: [
      // ── Stosy monet (małe) — blisko spawn'a, szybki onboarding ──
      {
        destructibleId: 'dest-coin-pile-s',
        positions: [
          { x: 1200, y: 650 },
          { x:  800, y: 850 },
          { x: 1100, y: 900 },
          { x:  900, y: 600 },
        ],
      },
      // ── Stosy monet (średnie) — średnia odległość ──
      {
        destructibleId: 'dest-coin-pile-m',
        positions: [
          { x: 1400, y: 500 },
          { x:  600, y: 700 },
          { x: 1300, y: 1000 },
        ],
      },
      // ── Stosy monet (duże) — dalej od spawn'a ──
      {
        destructibleId: 'dest-coin-pile-l',
        positions: [
          { x: 1600, y:  400 },
          { x:  400, y: 1100 },
        ],
      },
      // ── Skrzynie drewniane — średnia trudność ──
      {
        destructibleId: 'dest-chest-wood',
        positions: [
          { x: 1500, y: 800 },
          { x:  500, y: 500 },
        ],
      },
      // ── Skrzynia kamienna — wyższa trudność ──
      {
        destructibleId: 'dest-chest-stone',
        positions: [
          { x: 1700, y: 1100 },
        ],
      },
      // ── Kryształ — mini-boss, daleko od spawn'a, najlepszy reward + gemy ──
      {
        destructibleId: 'dest-crystal',
        positions: [
          { x: 300, y: 300 },
        ],
      },
    ],

    decorations: [
      // Dekoracje to wyłącznie wizual — bez kolizji, bez interakcji.
      // Rozrzucone po mapie żeby łąka nie była pusta.
      {
        textureKey: 'deco_flower_pink',
        positions: [
          { x:  200, y:  900 },
          { x:  700, y: 1200 },
          { x: 1100, y:  300 },
          { x: 1500, y: 1300 },
          { x: 1850, y:  700 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_flower_yellow',
        positions: [
          { x:  450, y:  300 },
          { x:  950, y: 1100 },
          { x: 1250, y:  800 },
          { x:  150, y:  600 },
          { x: 1700, y:  200 },
          { x:  850, y:  450 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_bush',
        positions: [
          { x:  100, y: 1300 },
          { x: 1900, y: 1400 },
          { x: 1100, y: 1400 },
          { x:  650, y:  150 },
          { x: 1850, y:  900 },
        ],
        scale: 1.1,
        randomFlip: true,
      },
    ],

    eggIds: ['egg-starter', 'egg-basic', 'egg-rare', 'egg-legendary'],

    teleportsTo: [],                       // MVP: brak teleportów (jeden biom)

    coinMultiplier: 1.0,                   // baseline tier-1
    exclusivePetIds: undefined,            // wszystkie 60 petów Meadow zdefiniowanych w pets.config.ts

    enabled: true,
  },

  // ═════════════════════════════════════════════════════════════════════════
  // MOUNTAIN — Biom 2 (tier 2). Skaliste góry, lodowe szczyty, śnieg.
  // 60 petów (53 golemy + 7 zwierząt), 6 destruktibli, 9 dekoracji.
  // ═════════════════════════════════════════════════════════════════════════
  {
    id: 'biome-mountain',
    name: { pl: 'Góry', en: 'Mountain Peaks' },
    tier: 2,

    unlock: {
      cost: { currency: 'coins', amount: biomeUnlockCost(2) },                 // 50_000
      requiredPower: biomePowerRequirement(2),                                  // 200
    },

    worldSize: { w: 2000, h: 1500 },
    playerSpawn: { x: 1000, y: 750 },

    scene: {
      backgroundTextureKey: 'bg_mountain_tile',
      backgroundColor: '#A8B0B3',          // pastelowy granitowy szarz — fallback i kolor pod teksturą
      bgmKey: undefined,
      ambientFx: 'snow',                   // Step 3.B.2 — 3-warstwowe padające płatki (patrz ambientFx.config.ts)
      atmosphere: 'mountain_cold',         // chłodna mroźna winieta + blade pionowe światło
      parallax: 'mountain_mist',           // wolna mroźna mgła na SCREEN overlayu (patrz parallax.config.ts)
      critters: 'mountain_critters',       // orzeł krążący wysoko + kozica przebiegająca nisko
    },

    destructibleSpawns: [
      // ── Zaśnieżone stosy monet (małe) — blisko spawn'a ──
      {
        destructibleId: 'dest-snow-coin-pile-s',
        positions: [
          { x: 1200, y: 650 },
          { x:  800, y: 850 },
          { x: 1100, y: 900 },
          { x:  900, y: 600 },
        ],
      },
      // ── Stosy (średnie) — średnia odległość ──
      {
        destructibleId: 'dest-snow-coin-pile-m',
        positions: [
          { x: 1400, y: 500 },
          { x:  600, y: 700 },
          { x: 1300, y: 1000 },
        ],
      },
      // ── Stosy (duże) — dalsze pola ──
      {
        destructibleId: 'dest-snow-coin-pile-l',
        positions: [
          { x: 1600, y:  400 },
          { x:  400, y: 1100 },
        ],
      },
      // ── Skalna skrzynia — średnia trudność ──
      {
        destructibleId: 'dest-mountain-rock-chest',
        positions: [
          { x: 1500, y: 800 },
          { x:  500, y: 500 },
        ],
      },
      // ── Lodowa skrzynia — wyższa trudność ──
      {
        destructibleId: 'dest-ice-chest',
        positions: [
          { x: 1700, y: 1100 },
        ],
      },
      // ── Lodowy kryształ — mini-boss, daleko od spawn'a ──
      {
        destructibleId: 'dest-ice-crystal',
        positions: [
          { x: 300, y: 300 },
        ],
      },
    ],

    decorations: [
      {
        textureKey: 'deco_mountain_grass',
        positions: [
          { x:  200, y:  900 },
          { x:  700, y: 1200 },
          { x: 1100, y:  300 },
          { x: 1500, y: 1300 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_snow_pile_small',
        positions: [
          { x:  450, y:  300 },
          { x: 1250, y:  800 },
          { x:  850, y:  450 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_snow_pile_large',
        positions: [
          { x:  100, y: 1300 },
          { x: 1900, y: 1400 },
        ],
        scale: 1.15,
      },
      {
        textureKey: 'deco_frost_bush',
        positions: [
          { x:  650, y:  150 },
          { x: 1850, y:  900 },
          { x: 1100, y: 1400 },
        ],
        scale: 1.1,
        randomFlip: true,
      },
      {
        textureKey: 'deco_alpine_flower_blue',
        positions: [
          { x:  950, y: 1100 },
          { x:  150, y:  600 },
          { x: 1700, y:  200 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_icy_rock',
        positions: [
          { x:  350, y:  800 },
          { x: 1750, y:  600 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_ice_crystal_cluster',
        positions: [
          { x:  550, y:  200 },
          { x: 1400, y: 1200 },
        ],
      },
      {
        textureKey: 'deco_snowy_pine_sapling',
        positions: [
          { x:  250, y:  450 },
          { x: 1800, y: 1200 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_ice_peak_cluster',
        positions: [
          { x:  100, y:  100 },
          { x: 1900, y:  100 },
        ],
        scale: 1.2,
      },
    ],

    eggIds: ['egg-mountain-starter', 'egg-mountain-basic', 'egg-mountain-rare', 'egg-mountain-legendary'],

    teleportsTo: [],                       // Step 3.B: dodać teleport meadow ↔ mountain
    coinMultiplier: 10.0,                  // tier 2 = ×10 vs meadow
    exclusivePetIds: undefined,            // 60 mountain petów zdefiniowanych w pets.config.ts

    enabled: true,
  },

  // ═════════════════════════════════════════════════════════════════════════
  // DZIKI ZACHÓD — Biom 3 (tier 3, dawniej Star Wars; ID zostają). Preria, kaktusy, kaniony.
  // 62 pety (60 standard + 2 mythic bossy Szeryf/Biały Bizonek), 6 destruktibli, 10 dekoracji.
  // ═════════════════════════════════════════════════════════════════════════
  {
    id: 'biome-starwars',
    name: { pl: 'Dziki Zachód', en: 'Wild West' },
    tier: 3,

    unlock: {
      cost: { currency: 'coins', amount: biomeUnlockCost(3) },                 // 750_000
      requiredPower: biomePowerRequirement(3),                                  // 2_000
    },

    worldSize: { w: 2000, h: 1500 },
    playerSpawn: { x: 1000, y: 750 },

    scene: {
      backgroundTextureKey: 'bg_starwars_tile',
      backgroundColor: '#D9C49B',          // pustynny piaskowy beż — fallback i kolor pod teksturą
      bgmKey: undefined,
      ambientFx: 'starwars',               // dryf piasku (3 warstwy — patrz ambientFx.config.ts)
      atmosphere: 'desert_heat',           // gorące pomarańczowe snopy + przypalona winieta (Tatooine)
      parallax: 'starwars_dust',           // tumany pyłu cieniujące piasek przez MULTIPLY (patrz parallax.config.ts)
      critters: 'starwars_critters',       // tumbleweed + szybki pustynny rider
    },

    destructibleSpawns: [
      // ── Kupki kredytów (małe) — blisko spawn'a ──
      {
        destructibleId: 'dest-starwars-credit-pile-s',
        positions: [
          { x: 1200, y: 650 },
          { x:  800, y: 850 },
          { x: 1100, y: 900 },
          { x:  900, y: 600 },
        ],
      },
      // ── Kupki kredytów (średnie) ──
      {
        destructibleId: 'dest-starwars-credit-pile-m',
        positions: [
          { x: 1400, y: 500 },
          { x:  600, y: 700 },
          { x: 1300, y: 1000 },
        ],
      },
      // ── Kupki kredytów (duże) ──
      {
        destructibleId: 'dest-starwars-credit-pile-l',
        positions: [
          { x: 1600, y:  400 },
          { x:  400, y: 1100 },
        ],
      },
      // ── Imperialna skrzynia — średnia trudność ──
      {
        destructibleId: 'dest-starwars-imperial-crate',
        positions: [
          { x: 1500, y: 800 },
          { x:  500, y: 500 },
        ],
      },
      // ── Sterta złomu droidów — wyższa trudność (gems) ──
      {
        destructibleId: 'dest-starwars-droid-scrap',
        positions: [
          { x: 1700, y: 1100 },
        ],
      },
      // ── Kryształ kybera — mini-boss, daleko od spawn'a (gems) ──
      {
        destructibleId: 'dest-starwars-kyber-crystal',
        positions: [
          { x: 300, y: 300 },
        ],
      },
    ],

    decorations: [
      {
        textureKey: 'deco_starwars_sand_dune',
        positions: [
          { x:  200, y:  900 },
          { x: 1500, y: 1300 },
          { x: 1850, y:  700 },
        ],
        scale: 1.15,
        randomFlip: true,
      },
      {
        textureKey: 'deco_starwars_sand_pile_small',
        positions: [
          { x:  450, y:  300 },
          { x: 1250, y:  800 },
          { x:  850, y:  450 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_starwars_sand_pile_large',
        positions: [
          { x:  100, y: 1300 },
          { x: 1900, y: 1400 },
        ],
        scale: 1.15,
      },
      {
        textureKey: 'deco_starwars_cactus_funaja',
        positions: [
          { x:  650, y:  150 },
          { x: 1100, y: 1400 },
          { x: 1700, y:  950 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_starwars_moisture_vaporator',
        positions: [
          { x:  950, y: 1100 },
          { x: 1700, y:  200 },
        ],
      },
      {
        textureKey: 'deco_starwars_rock_arch',
        positions: [
          { x:  150, y:  600 },
          { x: 1400, y: 1150 },
        ],
        scale: 1.2,
        randomFlip: true,
      },
      {
        textureKey: 'deco_starwars_satellite_dish',
        positions: [
          { x:  350, y: 1000 },
        ],
      },
      {
        textureKey: 'deco_starwars_at_at_footprint',
        positions: [
          { x:  750, y: 1250 },
          { x: 1600, y:  600 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_starwars_sandcrawler_track',
        positions: [
          { x: 1150, y:  300 },
        ],
      },
      {
        textureKey: 'deco_starwars_scrap_metal',
        positions: [
          { x:  550, y:  950 },
          { x: 1850, y: 1050 },
        ],
        randomFlip: true,
      },
    ],

    eggIds: ['egg-starwars-starter', 'egg-starwars-basic', 'egg-starwars-jedi', 'egg-starwars-force'],

    teleportsTo: [],
    coinMultiplier: 100.0,                 // tier 3 = ×100 vs meadow
    exclusivePetIds: undefined,            // 62 starwars petów zdefiniowanych w pets.config.ts

    enabled: true,
  },

  // ═════════════════════════════════════════════════════════════════════════
  // PIXEL WORLD — Biom 4 (tier 4). Świat pixelowy: mix retro gier, pełny pixel-art.
  // 62 pety (60 standard + 2 mythic bossy Król Glitchów/8-bit Bohater),
  // 6 destruktibli, 10 dekoracji.
  // ═════════════════════════════════════════════════════════════════════════
  {
    id: 'biome-pixel',
    name: { pl: 'Świat Pikseli', en: 'Pixel World' },
    tier: 4,

    unlock: {
      cost: { currency: 'coins', amount: biomeUnlockCost(4) },                 // 11_250_000
      requiredPower: biomePowerRequirement(4),                                  // 20_000
    },

    worldSize: { w: 2000, h: 1500 },
    playerSpawn: { x: 1000, y: 750 },

    scene: {
      backgroundTextureKey: 'bg_pixel_tile',
      backgroundColor: '#4FB23E',          // retro pixel grass — fallback i kolor pod teksturą
      bgmKey: undefined,
      ambientFx: 'pixel',                  // dryf unoszących się pixeli + glitch (patrz ambientFx.config.ts)
      atmosphere: 'pixel_crt',             // ramka kineskopu (mocna winieta) + neonowy puls, bez god rays
      parallax: 'pixel_neon',              // subtelne neonowe glitche ADD, z NEAREST filter po textureKey
      critters: 'pixel_critters',          // mały glitch-duch + ptak 8-bit
    },

    destructibleSpawns: [
      // ── Stosiki pixel-monet (małe) — blisko spawn'a ──
      {
        destructibleId: 'dest-pixel-coin-pile-s',
        positions: [
          { x: 1200, y: 650 },
          { x:  800, y: 850 },
          { x: 1100, y: 900 },
          { x:  900, y: 600 },
        ],
      },
      // ── Stosy (średnie) ──
      {
        destructibleId: 'dest-pixel-coin-pile-m',
        positions: [
          { x: 1400, y: 500 },
          { x:  600, y: 700 },
          { x: 1300, y: 1000 },
        ],
      },
      // ── Skarby (duże) ──
      {
        destructibleId: 'dest-pixel-coin-pile-l',
        positions: [
          { x: 1600, y:  400 },
          { x:  400, y: 1100 },
        ],
      },
      // ── Blok skarbu — średnia trudność ──
      {
        destructibleId: 'dest-pixel-loot-block',
        positions: [
          { x: 1500, y: 800 },
          { x:  500, y: 500 },
        ],
      },
      // ── Rdzeń danych — wyższa trudność (gems) ──
      {
        destructibleId: 'dest-pixel-data-core',
        positions: [
          { x: 1700, y: 1100 },
        ],
      },
      // ── Mega kryształ — mini-boss, daleko od spawn'a (gems) ──
      {
        destructibleId: 'dest-pixel-mega-crystal',
        positions: [
          { x: 300, y: 300 },
        ],
      },
    ],

    decorations: [
      {
        textureKey: 'deco_pixel_tree',
        positions: [
          { x:  200, y:  900 },
          { x: 1500, y: 1300 },
          { x: 1850, y:  700 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_pixel_bush',
        positions: [
          { x:  450, y:  300 },
          { x: 1250, y:  800 },
          { x:  850, y:  450 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_pixel_flower',
        positions: [
          { x:  700, y: 1200 },
          { x:  950, y: 1100 },
          { x:  150, y:  600 },
          { x: 1700, y:  200 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_pixel_rock',
        positions: [
          { x:  350, y:  800 },
          { x: 1750, y:  600 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_pixel_mushroom',
        positions: [
          { x:  650, y:  150 },
          { x: 1100, y: 1400 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_pixel_crystal_cluster',
        positions: [
          { x:  550, y:  200 },
          { x: 1400, y: 1200 },
        ],
      },
      {
        textureKey: 'deco_pixel_pipe',
        positions: [
          { x: 1150, y:  300 },
          { x:  250, y: 1100 },
        ],
      },
      {
        textureKey: 'deco_pixel_sign',
        positions: [
          { x:  750, y: 1250 },
        ],
      },
      {
        textureKey: 'deco_pixel_block',
        positions: [
          { x: 1600, y:  450 },
          { x: 1000, y:  250 },
        ],
      },
      {
        textureKey: 'deco_pixel_cloud',
        positions: [
          { x:  100, y:  100 },
          { x: 1900, y:  150 },
          { x: 1300, y:  650 },
        ],
        scale: 1.1,
      },
    ],

    eggIds: ['egg-pixel-starter', 'egg-pixel-basic', 'egg-pixel-arcade', 'egg-pixel-glitch'],

    teleportsTo: [],
    coinMultiplier: 1000.0,                // tier 4 = ×1000 vs meadow
    exclusivePetIds: undefined,            // 62 pixel petów zdefiniowanych w pets.config.ts

    enabled: true,
  },

  // ═════════════════════════════════════════════════════════════════════════
  // OCEAN — Biom 5 (tier 5). Głębiny morskie: dno oceanu, rafa koralowa,
  // tańczące kaustyki światła. 62 pety (60 standard + 2 mythic huge-only:
  // Wieloryb + Orka), 6 destruktibli, 10 dekoracji. Styl: GŁÓWNY (gładka naklejka PSX),
  // NIE pixel-art. basePower/ceny ×10 vs Pixel (×10000 vs Meadow).
  // ═════════════════════════════════════════════════════════════════════════
  {
    id: 'biome-ocean',
    name: { pl: 'Ocean', en: 'Ocean' },
    tier: 5,

    unlock: {
      cost: { currency: 'coins', amount: biomeUnlockCost(5) },                 // 168_750_000
      requiredPower: biomePowerRequirement(5),                                  // 200_000
    },

    worldSize: { w: 2000, h: 1500 },
    playerSpawn: { x: 1000, y: 750 },

    scene: {
      backgroundTextureKey: 'bg_ocean_tile',
      backgroundColor: '#2C7A9E',          // morski błękit dna — fallback i kolor pod teksturą
      bgmKey: undefined,
      ambientFx: 'ocean',                  // dryf bąbelków (2 warstwy) + świetlne kaustyki (patrz ambientFx.config.ts)
      atmosphere: 'ocean_depths',          // granatowa winieta głębin + cyjanowe snopy kaustyk
      parallax: 'ocean_caustics',          // ruchoma sieć światła na dnie (SCREEN, patrz parallax.config.ts)
      critters: 'ocean_critters',          // drobna ryba / meduza / konik morski
    },

    destructibleSpawns: [
      // ── Kupki pereł (małe) — blisko spawn'a ──
      {
        destructibleId: 'dest-ocean-pearl-pile-s',
        positions: [
          { x: 1200, y: 650 },
          { x:  800, y: 850 },
          { x: 1100, y: 900 },
          { x:  900, y: 600 },
        ],
      },
      // ── Kupki pereł (średnie) ──
      {
        destructibleId: 'dest-ocean-pearl-pile-m',
        positions: [
          { x: 1400, y: 500 },
          { x:  600, y: 700 },
          { x: 1300, y: 1000 },
        ],
      },
      // ── Kupki pereł (duże) ──
      {
        destructibleId: 'dest-ocean-pearl-pile-l',
        positions: [
          { x: 1600, y:  400 },
          { x:  400, y: 1100 },
        ],
      },
      // ── Zatopiona skrzynia — średnia trudność ──
      {
        destructibleId: 'dest-ocean-treasure-chest',
        positions: [
          { x: 1500, y: 800 },
          { x:  500, y: 500 },
        ],
      },
      // ── Olbrzymi małż — wyższa trudność (gems) ──
      {
        destructibleId: 'dest-ocean-giant-clam',
        positions: [
          { x: 1700, y: 1100 },
        ],
      },
      // ── Koralowy kryształ — mini-boss, daleko od spawn'a (gems) ──
      {
        destructibleId: 'dest-ocean-coral-crystal',
        positions: [
          { x: 300, y: 300 },
        ],
      },
    ],

    decorations: [
      {
        textureKey: 'deco_ocean_coral_pink',
        positions: [
          { x:  200, y:  900 },
          { x: 1500, y: 1300 },
          { x: 1850, y:  700 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_ocean_coral_blue',
        positions: [
          { x:  450, y:  300 },
          { x: 1250, y:  800 },
          { x:  850, y:  450 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_ocean_brain_coral',
        positions: [
          { x:  700, y: 1200 },
          { x: 1700, y:  950 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_ocean_kelp',
        positions: [
          { x:  100, y: 1300 },
          { x: 1900, y: 1400 },
          { x: 1100, y: 1400 },
        ],
        scale: 1.15,
        randomFlip: true,
      },
      {
        textureKey: 'deco_ocean_seagrass',
        positions: [
          { x:  950, y: 1100 },
          { x:  150, y:  600 },
          { x: 1700, y:  200 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_ocean_anemone',
        positions: [
          { x:  650, y:  150 },
          { x: 1400, y: 1150 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_ocean_sea_rock',
        positions: [
          { x:  350, y:  800 },
          { x: 1750, y:  600 },
        ],
        scale: 1.1,
        randomFlip: true,
      },
      {
        textureKey: 'deco_ocean_shell',
        positions: [
          { x:  550, y:  950 },
          { x: 1850, y: 1050 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_ocean_sea_star',
        positions: [
          { x: 1150, y:  300 },
          { x:  250, y: 1100 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_ocean_bubble_vent',
        positions: [
          { x:  100, y:  100 },
          { x: 1900, y:  100 },
        ],
      },
    ],

    eggIds: ['egg-ocean-starter', 'egg-ocean-basic', 'egg-ocean-deep', 'egg-ocean-abyss'],

    teleportsTo: [],
    coinMultiplier: 10000.0,               // tier 5 = ×10000 vs meadow
    exclusivePetIds: undefined,            // 62 ocean petów zdefiniowanych w pets.config.ts

    enabled: true,
  },

  // ═════════════════════════════════════════════════════════════════════════
  // FOOD — Biom 6 (tier 6). Fast Food / Świat Jedzenia: blat baru, frytki,
  // burgery, słodycze. 62 pety (60 standard + 2 mythic huge-only: Piesburger +
  // Lodówka), 6 destruktibli, 10 dekoracji. Styl: GŁÓWNY (gładka naklejka PSX),
  // NIE pixel-art. basePower/ceny ×10 vs Ocean (×100000 vs Meadow).
  // ═════════════════════════════════════════════════════════════════════════
  {
    id: 'biome-food',
    name: { pl: 'Fast Food', en: 'Food World' },
    tier: 6,

    unlock: {
      cost: { currency: 'coins', amount: biomeUnlockCost(6) },                 // 2_531_250_000
      requiredPower: biomePowerRequirement(6),                                  // 2_000_000
    },

    worldSize: { w: 2000, h: 1500 },
    playerSpawn: { x: 1000, y: 750 },

    scene: {
      backgroundTextureKey: 'bg_food_tile',
      backgroundColor: '#F2C879',          // ciepły maślano-serowy blat — fallback i kolor pod teksturą
      bgmKey: undefined,
      ambientFx: 'food',                   // dryf okruszków (2 warstwy) + kolorowa posypka (patrz ambientFx.config.ts)
      atmosphere: 'food_diner',            // ciepła bursztynowa winieta + miodowe światło lampy nad blatem
      parallax: 'food_steam',              // unoszące się smugi pary/aromatu (SCREEN, patrz parallax.config.ts)
      critters: 'food_critters',           // skacząca frytka + pszczółka lecąca do słodkości
    },

    destructibleSpawns: [
      // ── Porcje frytek (małe) — blisko spawn'a ──
      {
        destructibleId: 'dest-food-fries-s',
        positions: [
          { x: 1200, y: 650 },
          { x:  800, y: 850 },
          { x: 1100, y: 900 },
          { x:  900, y: 600 },
        ],
      },
      // ── Porcje frytek (średnie) ──
      {
        destructibleId: 'dest-food-fries-m',
        positions: [
          { x: 1400, y: 500 },
          { x:  600, y: 700 },
          { x: 1300, y: 1000 },
        ],
      },
      // ── Porcje frytek (duże) ──
      {
        destructibleId: 'dest-food-fries-l',
        positions: [
          { x: 1600, y:  400 },
          { x:  400, y: 1100 },
        ],
      },
      // ── Pudełko śniadaniowe — średnia trudność ──
      {
        destructibleId: 'dest-food-lunchbox',
        positions: [
          { x: 1500, y: 800 },
          { x:  500, y: 500 },
        ],
      },
      // ── Automat z przekąskami — wyższa trudność (gems) ──
      {
        destructibleId: 'dest-food-vending',
        positions: [
          { x: 1700, y: 1100 },
        ],
      },
      // ── Wielki tort — mini-boss, daleko od spawn'a (gems) ──
      {
        destructibleId: 'dest-food-cake',
        positions: [
          { x: 300, y: 300 },
        ],
      },
    ],

    decorations: [
      {
        textureKey: 'deco_food_ketchup',
        positions: [
          { x:  200, y:  900 },
          { x: 1500, y: 1300 },
          { x: 1850, y:  700 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_food_mustard',
        positions: [
          { x:  450, y:  300 },
          { x: 1250, y:  800 },
          { x:  850, y:  450 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_food_straw_cup',
        positions: [
          { x:  700, y: 1200 },
          { x: 1700, y:  950 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_food_tomato_plant',
        positions: [
          { x:  100, y: 1300 },
          { x: 1900, y: 1400 },
          { x: 1100, y: 1400 },
        ],
        scale: 1.15,
        randomFlip: true,
      },
      {
        textureKey: 'deco_food_fries_box',
        positions: [
          { x:  950, y: 1100 },
          { x:  150, y:  600 },
          { x: 1700, y:  200 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_food_pizza_box',
        positions: [
          { x:  650, y:  150 },
          { x: 1400, y: 1150 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_food_salt_pepper',
        positions: [
          { x:  350, y:  800 },
          { x: 1750, y:  600 },
        ],
        scale: 1.1,
        randomFlip: true,
      },
      {
        textureKey: 'deco_food_cupcake_stand',
        positions: [
          { x:  550, y:  950 },
          { x: 1850, y: 1050 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_food_napkin_stack',
        positions: [
          { x: 1150, y:  300 },
          { x:  250, y: 1100 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_food_soda_can',
        positions: [
          { x:  100, y:  100 },
          { x: 1900, y:  100 },
        ],
        randomFlip: true,
      },
    ],

    eggIds: ['egg-food-starter', 'egg-food-basic', 'egg-food-deluxe', 'egg-food-feast'],

    teleportsTo: [],
    coinMultiplier: 100000.0,              // tier 6 = ×100000 vs meadow
    exclusivePetIds: undefined,            // 62 food petów zdefiniowanych w pets.config.ts

    enabled: true,
  },

  // ═════════════════════════════════════════════════════════════════════════
  // ATLANTYDA — Biom 7 (tier 7). Świątynia Posejdona w zatopionej Atlantydzie:
  // posadzka i kolumny z orichalku, rzeźby z kości słoniowej, syreny, trytony,
  // mityczne stwory głębin. 63 pety (60 standard + 3 mythic huge-only bossy:
  // Posejdon + Tryton + Kraken = „Władcy Atlantydy"), 7 destruktibli (z Sejfem),
  // 11 dekoracji (z fasadą świątyni). ATL-1: mapa 2800×1900, 5 stref (Ruiny →
  // Święta Droga → Świątynia → Skarbiec → Otchłań), spawn w Ruinach.
  // basePower/ceny ×10 vs Food (×1000000 vs Meadow).
  //
  // ⭐ GO-LIVE 2026-06-17 — enabled:true (linia niżej). Biom WPROWADZONY: assety
  //    (97 webp + 4 jajka) wgrane, serwerowy SERVER_BIOMES['biome-atlantis'] (tier 7)
  //    dodany, eggs.ts przegenerowany. Widoczny i odblokowywalny w BiomeMapModal.
  //    Rollback: usuń wpis z SERVER_BIOMES ORAZ ustaw enabled:false.
  // ═════════════════════════════════════════════════════════════════════════
  {
    id: 'biome-atlantis',
    name: { pl: 'Atlantyda', en: 'Atlantis' },
    tier: 7,

    unlock: {
      cost: { currency: 'coins', amount: biomeUnlockCost(7) },                 // 37_968_750_000
      requiredPower: biomePowerRequirement(7),                                  // 20_000_000
    },

    worldSize: { w: 2800, h: 1900 },      // ATL-1: większa mapa pod 5 stref
    playerSpawn: { x: 550, y: 1500 },     // ATL-1: spawn w Ruinach (SW)

    scene: {
      backgroundTextureKey: 'bg_atlantis_tile',
      backgroundColor: '#2C8079',          // patynowany kamień świątyni (posadzka kość słoniowa + orichalcum)
      bgmKey: undefined,
      ambientFx: 'atlantis',               // bąbelki (2 warstwy) + złota iskierka
      atmosphere: 'atlantis_depths',       // turkusowa winieta głębin + złote god-rays
      parallax: 'atlantis_godrays',        // dryfujące złote smugi boskiego światła (SCREEN)
      critters: 'atlantis_critters',       // ławica rybek + meduza + delfin (ATL-1)
    },

    destructibleSpawns: [
      // ════ Amfory MAŁE — RUINY (start, SW) + lekka Zatoka (SE) ════
      {
        destructibleId: 'dest-atlantis-amphora-s',
        positions: [
          { x:  450, y: 1400 },
          { x:  650, y: 1560 },
          { x:  800, y: 1380 },
          { x:  380, y: 1640 },
          { x: 2150, y: 1500 },
          { x: 2470, y: 1400 },
        ],
      },
      // ════ Amfory ŚREDNIE — Ruiny + Święta Droga + Otchłań + Zatoka ════
      {
        destructibleId: 'dest-atlantis-amphora-m',
        positions: [
          { x:  760, y: 1690 },
          { x:  940, y: 1470 },
          { x:  990, y: 1300 },
          { x: 1190, y: 1150 },
          { x:  300, y:  660 },
          { x: 2300, y: 1660 },
        ],
      },
      // ════ Amfory DUŻE — SKARBIEC (NE) + Otchłań (NW) ════
      {
        destructibleId: 'dest-atlantis-amphora-l',
        positions: [
          { x: 2100, y:  400 },
          { x: 2460, y:  350 },
          { x:  720, y:  600 },
        ],
      },
      // ════ Koralowe skrzynie — Święta Droga + SKARBIEC ════
      {
        destructibleId: 'dest-atlantis-coral-chest',
        positions: [
          { x:  860, y: 1250 },
          { x: 2250, y:  610 },
          { x: 2520, y:  560 },
        ],
      },
      // ════ Posągi strażników — ŚWIĄTYNIA (centrum), flankują fasadę (gems) ════
      {
        destructibleId: 'dest-atlantis-guardian-statue',
        positions: [
          { x: 1180, y:  960 },
          { x: 1620, y:  960 },
        ],
      },
      // ════ Ołtarze trójzębu — mini-bossy (gems). Świątynia + Otchłań (daleko) ════
      {
        destructibleId: 'dest-atlantis-trident-altar',
        positions: [
          { x: 1400, y:  720 },
          { x:  400, y:  350 },
        ],
      },
      // ════ SEJF ATLANTYDY — mega-cel (6 petów), głęboko w Skarbcu (gems) ════
      {
        destructibleId: 'dest-atlantis-vault',
        positions: [
          { x: 2320, y:  250 },
        ],
      },
    ],

    decorations: [
      // Landmark: FASADA ŚWIĄTYNI POSEJDONA (centrum, duża) — serce mapy
      {
        textureKey: 'deco_atlantis_temple_facade',
        positions: [{ x: 1400, y: 900 }],
        scale: 1.8,
      },
      { textureKey: 'deco_atlantis_column', positions: [
          { x: 1140, y: 780 }, { x: 1660, y: 780 }, { x: 1140, y: 1120 }, { x: 1660, y: 1120 }, { x: 1000, y: 1250 },
        ], randomFlip: true },
      { textureKey: 'deco_atlantis_column_broken', positions: [
          { x: 380, y: 1350 }, { x: 720, y: 1660 }, { x: 300, y: 520 }, { x: 860, y: 400 },
        ], randomFlip: true },
      { textureKey: 'deco_atlantis_poseidon_statue', positions: [
          { x: 1400, y: 1200 }, { x: 2180, y: 520 },
        ], randomFlip: true },
      { textureKey: 'deco_atlantis_arch', positions: [
          { x: 1230, y: 1290 }, { x: 1980, y: 980 },
        ], scale: 1.2 },
      { textureKey: 'deco_atlantis_brazier', positions: [
          { x: 840, y: 1430 }, { x: 1010, y: 1290 }, { x: 1190, y: 1140 }, { x: 1330, y: 1030 }, { x: 1190, y: 860 }, { x: 1620, y: 860 },
        ], randomFlip: true },
      { textureKey: 'deco_atlantis_amphora', positions: [
          { x: 2420, y: 720 }, { x: 2120, y: 300 }, { x: 2280, y: 1560 },
        ], randomFlip: true },
      { textureKey: 'deco_atlantis_trident_emblem', positions: [
          { x: 1400, y: 640 }, { x: 560, y: 250 },
        ], scale: 1.1, randomFlip: true },
      { textureKey: 'deco_atlantis_treasure', positions: [
          { x: 2360, y: 460 }, { x: 2540, y: 660 },
        ], randomFlip: true },
      { textureKey: 'deco_atlantis_giant_shell', positions: [
          { x: 2120, y: 1700 }, { x: 2520, y: 1260 }, { x: 320, y: 760 },
        ], randomFlip: true },
      { textureKey: 'deco_atlantis_sacred_basin', positions: [
          { x: 1120, y: 1000 }, { x: 1680, y: 1000 }, { x: 1400, y: 1260 },
        ], scale: 1.15, randomFlip: true },
    ],

    eggIds: ['egg-atlantis-starter', 'egg-atlantis-basic', 'egg-atlantis-deluxe', 'egg-atlantis-royal'],

    teleportsTo: [],
    coinMultiplier: 1000000.0,             // tier 7 = ×1000000 vs meadow
    exclusivePetIds: undefined,            // 63 atlantis petów zdefiniowanych w pets.config.ts

    enabled: true,                         // ⭐ GO-LIVE 2026-06-17 (razem z SERVER_BIOMES['biome-atlantis'])
  },

  // ═════════════════════════════════════════════════════════════════════════
  // SZKOŁA — Biom 8 (tier 8). Nowy rok szkolny: gracz jest mały jak gumka-myszka
  // i chodzi po arkuszu papieru w kratkę rozłożonym na szkolnej ławce; wszystko
  // dookoła to przybory w skali „olbrzym". 63 pety (60 standard + 3 mythic
  // huge-only bossy: Król Ołówek + Tornister Olbrzym + Wielki Dzwonek =
  // „Mistrzowie Szkoły"), 7 destruktibli (z Dzwonkiem jako mega-celem),
  // 11 dekoracji. Styl GŁÓWNY (gładka naklejka PSX), NIE pixel-art.
  // basePower/ceny ×10 vs Atlantyda (×10000000 vs Meadow).
  //
  // ⭐ GO-LIVE 2026-08-18 — enabled:true (linia na końcu bloku). Biom WPROWADZONY:
  //    assety (92 png+webp + 63 plakaty) wgrane, serwerowy SERVER_BIOMES['biome-school']
  //    (tier 8) dodany, eggs.ts przegenerowany, 'school' dopisane do MINT_BIOMES.
  //    Widoczny i odblokowywalny w BiomeMapModal.
  //    ⚠️ Gdyby trzeba było rozdzielić — SERWER PIERWSZY. Odwrotna kolejność
  //    podnosi klientowy `MAX_COINS_PER_TICK` (liczony z ENABLED_BIOMES) ponad
  //    serwerowy (liczony z SERVER_BIOMES) → tick odbity 400 → resend w kółko →
  //    zatkany pipeline monet. Patrz komentarz w AutoSaveBridge.ts + §3.1
  //    DESIGN_BIOME_SCHOOL.md.
  //    Rollback: usuń wpis z SERVER_BIOMES ORAZ ustaw enabled:false.
  // ═════════════════════════════════════════════════════════════════════════
  {
    id: 'biome-school',
    name: { pl: 'Szkoła', en: 'School' },
    tier: 8,

    unlock: {
      cost: { currency: 'coins', amount: biomeUnlockCost(8) },                 // 569_531_250_000
      requiredPower: biomePowerRequirement(8),                                  // 200_000_000
    },

    worldSize: { w: 2000, h: 1500 },
    playerSpawn: { x: 1000, y: 750 },

    scene: {
      backgroundTextureKey: 'bg_school_tile',
      backgroundColor: '#F2EAD3',          // ciepły papierowy krem (kartka w kratkę)
      bgmKey: undefined,
      ambientFx: 'school',                 // pyłek kredy (2 warstwy) + złote gwiazdki
      atmosphere: 'school_morning',        // ciepła winieta sali + miodowe god-rays
      parallax: 'school_sunbeams',         // plamy światła z okien (SCREEN)
      critters: 'school_critters',         // samolocik + kleksik + książkowy robaczek
    },

    // Proceduralny fundament zostaje tylko pod wielką kartką-klasą i jej
    // korytarzem. Sekcje tematyczne oraz oba podesty są osobnymi papierowymi
    // assetami (`deco_school_zone_*`) — dzięki temu mają fakturę, taśmę i doodle.
    groundMarks: [
      {
        kind: 'roundedRect',
        x: 1000, y: 760, width: 1100, height: 1160, radius: 44,
        fillColor: 0xfff7e3, fillAlpha: 0.78,
        strokeColor: 0xb8783d, strokeAlpha: 0.82, strokeWidth: 9,
        shadow: { offsetX: 16, offsetY: 20, color: 0x654726, alpha: 0.18 },
      },
      {
        kind: 'roundedRect',
        x: 1000, y: 760, width: 1060, height: 1120, radius: 36,
        fillColor: 0xffffff, fillAlpha: 0,
        strokeColor: 0x9ebbd4, strokeAlpha: 0.42, strokeWidth: 3,
      },
      // Czerwona linia marginesu zamienia centralny panel w wielką kartkę zeszytu.
      {
        kind: 'polyline',
        points: [{ x: 520, y: 240 }, { x: 520, y: 1280 }],
        color: 0xd95e45, alpha: 0.55, width: 5,
      },
      // Dywan / główny korytarz klasy — otwarta przestrzeń dla gracza.
      {
        kind: 'roundedRect',
        x: 1000, y: 900, width: 260, height: 690, radius: 70,
        fillColor: 0xbfdcf0, fillAlpha: 0.27,
        strokeColor: 0x6d9fc3, strokeAlpha: 0.42, strokeWidth: 5,
      },
    ],

    destructibleSpawns: [
      // ── Małe zeszyty leżą na czterech ławkach wokół wolnego korytarza ──
      {
        destructibleId: 'dest-school-notebook-stack-s',
        positions: [
          { x:  710, y:  650 },
          { x: 1290, y:  650 },
          { x:  710, y:  980 },
          { x: 1290, y:  980 },
        ],
      },
      // ── Średnie cele prowadzą do biblioteki, nauki i końca głównej alejki ──
      {
        destructibleId: 'dest-school-notebook-stack-m',
        positions: [
          { x:  245, y:  535 },
          { x: 1755, y:  585 },
          { x: 1000, y: 1220 },
        ],
      },
      // ── Duże zeszyty pilnują dwóch dolnych pracowni ──
      {
        destructibleId: 'dest-school-notebook-stack-l',
        positions: [
          { x:  245, y: 1080 },
          { x: 1755, y: 1080 },
        ],
      },
      // ── Piórniki domykają plastyczny i szatniowy kącik ──
      {
        destructibleId: 'dest-school-pencil-case',
        positions: [
          { x:  330, y: 1245 },
          { x: 1670, y: 1245 },
        ],
      },
      // ── Plecak Pierwszaka — wyższa trudność (gems) ──
      {
        destructibleId: 'dest-school-backpack',
        positions: [
          { x: 1840, y: 1280 },
        ],
      },
      // ── Globus — mini-boss i kotwica biblioteki ──
      {
        destructibleId: 'dest-school-globe',
        positions: [
          { x: 220, y: 315 },
        ],
      },
      // ── Dzwonek Szkolny — mega-cel na własnym złotym podeście ──
      {
        destructibleId: 'dest-school-school-bell',
        positions: [
          { x: 1770, y: 165 },
        ],
      },
    ],

    decorations: [
      // Papierowe maty tematyczne — wygenerowane z przezroczystym tłem.
      // Delikatne doodle na obrzeżach komunikują funkcję strefy bez napisów.
      {
        textureKey: 'deco_school_zone_library',
        positions: [{ x: 230, y: 440 }],
        displaySize: { w: 400, h: 550 },
        depth: -89,
        animated: false,
      },
      {
        textureKey: 'deco_school_zone_science',
        positions: [{ x: 1770, y: 510 }],
        displaySize: { w: 400, h: 550 },
        depth: -89,
        animated: false,
      },
      {
        textureKey: 'deco_school_zone_art',
        positions: [{ x: 230, y: 1150 }],
        displaySize: { w: 360, h: 500 },
        depth: -89,
        animated: false,
      },
      {
        textureKey: 'deco_school_zone_gym',
        positions: [{ x: 1770, y: 1160 }],
        displaySize: { w: 360, h: 500 },
        depth: -89,
        animated: false,
      },
      {
        textureKey: 'deco_school_teacher_rug',
        positions: [{ x: 1000, y: 455 }],
        displaySize: { w: 500, h: 250 },
        depth: -88,
        animated: false,
      },
      {
        textureKey: 'deco_school_bell_rosette',
        positions: [{ x: 1770, y: 165 }],
        displaySize: { w: 230, h: 230 },
        depth: -88,
        animated: false,
      },
      // Biurko nauczyciela — największy mebel i główny landmark klasy.
      {
        textureKey: 'deco_school_big_desk',
        positions: [{ x: 1000, y: 455 }],
        displaySize: { w: 320, h: 215 },
        depth: -47,
        animated: false,
      },
      // Cztery ławki uczniów tworzą dwie równe aleje zamiast jednej luźnej ikony.
      {
        textureKey: 'deco_school_big_desk',
        positions: [
          { x:  710, y:  700 },
          { x: 1290, y:  700, flipX: true },
          { x:  710, y: 1030, flipX: true },
          { x: 1290, y: 1030 },
        ],
        displaySize: { w: 245, h: 165 },
        depth: -48,
        animated: false,
      },
      // Dwa wielkie ołówki są wizualnymi „ścianami” centralnej kartki.
      {
        textureKey: 'deco_school_pencil',
        positions: [
          { x:  470, y:  735, rotationDeg:  74 },
          { x: 1530, y:  735, rotationDeg: -74, flipX: true },
          { x: 1000, y: 1370, rotationDeg:   6 },
        ],
        displaySize: { w: 255, h: 120 },
        depth: -56,
        animated: false,
      },
      // Biblioteka: książki są skupione razem, więc miejsce czyta się bez etykiety.
      {
        textureKey: 'deco_school_book_stack',
        positions: [
          { x: 130, y: 470, rotationDeg: -5 },
          { x: 340, y: 390, rotationDeg:  7, flipX: true },
          { x: 345, y: 580, rotationDeg: -3 },
        ],
        displaySize: { w: 105, h: 110 },
        depth: -46,
        animated: false,
      },
      // Otwarte książki ożywiają bibliotekę i biurko nauczyciela. Osobna
      // kartka obraca się wokół grzbietu; wspólny system nie animuje ich naraz.
      {
        textureKey: 'deco_school_open_book',
        positions: [
          { x: 110, y: 225, rotationDeg: -6 },
          { x: 185, y: 665, rotationDeg:  5 },
          { x: 925, y: 455, rotationDeg: -3 },
        ],
        displaySize: { w: 158, h: 105 },
        depth: -45,
        animated: false,
        motion: {
          kind: 'pageFlip',
          pageTextureKey: 'deco_school_book_page',
          pageWidthRatio: 0.72,
          pageHeightRatio: 0.92,
          hingeOriginX: 0.178,
          liftPx: 7,
          durationMs: 820,
          intervalMinMs: 4_500,
          intervalMaxMs: 9_000,
          interactive: true,
        },
      },
      // Plastyka: kredki, farby i jabłko tworzą jeden kolorowy klaster.
      {
        textureKey: 'deco_school_crayon_box',
        positions: [
          { x: 135, y: 1075, rotationDeg: -6 },
          { x: 370, y: 1160, rotationDeg:  5, flipX: true },
        ],
        displaySize: { w: 112, h: 108 },
        depth: -45,
        animated: false,
      },
      {
        textureKey: 'deco_school_chalk_pile',
        positions: [
          { x:  830, y: 330, rotationDeg: -8 },
          { x: 1170, y: 330, rotationDeg:  6, flipX: true },
          { x: 1650, y: 420, rotationDeg: -5 },
        ],
        displaySize: { w: 82, h: 60 },
        depth: -44,
        animated: false,
      },
      {
        textureKey: 'deco_school_paint_set',
        positions: [
          { x: 125, y: 1240, rotationDeg: -4 },
          { x: 365, y: 1320, rotationDeg:  5, flipX: true },
        ],
        displaySize: { w: 115, h: 115 },
        depth: -44,
        animated: false,
      },
      // Kącik naukowy: duże przyrządy zamiast małych losowych ikonek.
      {
        textureKey: 'deco_school_ruler_set',
        positions: [
          { x: 1660, y: 505, rotationDeg: -12 },
          { x: 1850, y: 665, rotationDeg:   9, flipX: true },
        ],
        displaySize: { w: 132, h: 110 },
        depth: -45,
        animated: false,
      },
      {
        textureKey: 'deco_school_paper_plane',
        positions: [
          { x:  390, y: 170, rotationDeg: -14 },
          { x: 1600, y: 910, rotationDeg:  18, flipX: true },
        ],
        displaySize: { w: 105, h: 76 },
        depth: -53,
        animated: false,
      },
      {
        textureKey: 'deco_school_apple',
        positions: [
          { x: 1135, y: 455, rotationDeg:  4 },
          { x:  110, y: 1350, rotationDeg: -5, flipX: true },
        ],
        displaySize: { w: 76, h: 82 },
        depth: -43,
        animated: false,
      },
      // Jedna duża tablica definiuje front klasy; mniejsza oznacza pracownię naukową.
      {
        textureKey: 'deco_school_blackboard',
        positions: [
          { x: 1000, y: 245 },
        ],
        displaySize: { w: 340, h: 230 },
        depth: -45,
        animated: false,
      },
      {
        textureKey: 'deco_school_blackboard',
        positions: [{ x: 1770, y: 385 }],
        displaySize: { w: 155, h: 125 },
        depth: -45,
        animated: false,
      },
      {
        textureKey: 'deco_school_wall_clock',
        positions: [{ x: 1000, y: 82 }],
        displaySize: { w: 100, h: 100 },
        depth: -44,
        animated: false,
      },
    ],

    eggIds: ['egg-school-starter', 'egg-school-basic', 'egg-school-honors', 'egg-school-graduation'],

    teleportsTo: [],
    coinMultiplier: 10000000.0,            // tier 8 = ×10000000 vs meadow
    exclusivePetIds: undefined,            // 63 school petów zdefiniowanych w pets.config.ts

    enabled: true,                         // ⭐ GO-LIVE 2026-08-18 (razem z SERVER_BIOMES['biome-school'])
  },

  // ═════════════════════════════════════════════════════════════════════════
  // ARKTYKA — Biom 9 (zwykły, competitive). Rozległa morska kra pod wieczorną
  // zorzą, z obozem ekspedycji pośrodku, Zatoką Foczek na zachodzie, Ogrodem
  // Lodowców na wschodzie i Koroną Zorzy na północy. Celowo odróżnia się od
  // Gór: bez alpejskich ścian/pinów; motywami są morze, pękająca kra, fauna
  // polarna i kolorowe światło zorzy. 60 standard + 5 Huge-only.
  //
  // ⭐ GO-LIVE 2026-09-04 — komplet 163 assetów PNG/WebP i ich promptów,
  // 65 plakatów Banku, serwerowy SERVER_BIOMES tier 9, cztery aktywne jajka,
  // origin picker, Album i katalog botów są wdrożone razem. Przy rozdzielonym
  // deployu serwer nadal musi wjechać pierwszy; rollback wymaga wyłączenia
  // klienta i usunięcia wpisu serwera w tej samej wersji.
  // ═════════════════════════════════════════════════════════════════════════
  {
    id: 'biome-arctic',
    name: { pl: 'Arktyka', en: 'Arctic' },
    tier: 9,

    unlock: {
      cost: { currency: 'coins', amount: biomeUnlockCost(9) },                  // 8_542_968_750_000
      requiredPower: biomePowerRequirement(9),                                  // 2_000_000_000
      // Bez requiredBiomeId: backend dziś nie egzekwuje takiej bramki. Kolejność
      // progresji wyraża tier + koszt + moc, bez pozornego zabezpieczenia w UI.
    },

    worldSize: { w: 2200, h: 1600 },
    playerSpawn: { x: 1100, y: 800 },

    scene: {
      backgroundTextureKey: 'bg_arctic_tile',
      backgroundColor: '#D9F2F4',          // jasna morska kra — fallback bez utraty czytelności
      bgmKey: undefined,
      ambientFx: 'arctic',                 // dwie warstwy śniegu + rzadkie iskry lodu
      atmosphere: 'arctic_twilight',       // chłodna winieta + cyjanowo-fioletowa zorza
      parallax: 'arctic_aurora',           // SCREEN zorza + MULTIPLY cienie chmur
      critters: 'arctic_critters',         // sowa, ślizgająca się foczka, lisek
    },

    // Layout v2: proceduralne figury nie wyznaczają już zamkniętych „aren”.
    // Organiczne strefy budują cztery duże decale kry na depth -89. Tutaj
    // zostają tylko trzy spokojne, półprzezroczyste szlaki nawigacyjne między
    // klastrami. Dzięki temu mapa ma rytm atrakcji i odpoczynku bez debugowych
    // okręgów ani nadmiaru bodźców.
    groundMarks: [
      {
        kind: 'polyline',
        points: [
          { x: 900, y: 700 }, { x: 760, y: 620 }, { x: 615, y: 640 },
          { x: 515, y: 735 },
        ],
        color: 0x6fc6d8, alpha: 0.24, width: 13,
        outlineColor: 0xeefcff, outlineAlpha: 0.48, outlineWidth: 3,
      },
      {
        kind: 'polyline',
        points: [
          { x: 1300, y: 700 }, { x: 1450, y: 610 }, { x: 1595, y: 630 },
          { x: 1690, y: 720 },
        ],
        color: 0x6fc6d8, alpha: 0.24, width: 13,
        outlineColor: 0xeefcff, outlineAlpha: 0.48, outlineWidth: 3,
      },
      {
        kind: 'polyline',
        points: [
          { x: 1100, y: 530 }, { x: 1075, y: 445 }, { x: 1100, y: 345 },
        ],
        color: 0x927de3, alpha: 0.18, width: 11,
        outlineColor: 0xeefcff, outlineAlpha: 0.42, outlineWidth: 3,
      },
    ],

    destructibleSpawns: [
      {
        destructibleId: 'dest-arctic-snowdrift-s',
        positions: [
          { x: 900, y: 760 }, { x: 1300, y: 760 },
          { x: 930, y: 1040 }, { x: 1270, y: 1040 },
        ],
      },
      {
        destructibleId: 'dest-arctic-snowdrift-m',
        positions: [
          { x: 610, y: 600 }, { x: 1590, y: 610 }, { x: 1100, y: 1160 },
        ],
      },
      {
        destructibleId: 'dest-arctic-snowdrift-l',
        positions: [{ x: 365, y: 1110 }, { x: 1820, y: 1130 }],
      },
      {
        destructibleId: 'dest-arctic-frozen-fish-crate',
        positions: [{ x: 500, y: 900 }, { x: 1430, y: 990 }],
      },
      {
        destructibleId: 'dest-arctic-expedition-chest',
        positions: [{ x: 790, y: 1185 }],
      },
      {
        destructibleId: 'dest-arctic-aurora-crystal',
        positions: [{ x: 1900, y: 760 }],
      },
      {
        destructibleId: 'dest-arctic-ancient-iceberg',
        positions: [{ x: 1100, y: 185 }],
      },
    ],

    decorations: [
      // Cztery duże naturalne płaty kry zastępują proceduralne okręgi v1.
      // Są statyczne i niskokontrastowe; otwarte środki zostawiają spokojne
      // miejsce na rozgrywkę, a bardziej szczegółowe brzegi skupiają uwagę.
      {
        textureKey: 'deco_arctic_floe_camp',
        positions: [{ x: 1100, y: 825 }],
        displaySize: { w: 920, h: 690 }, depth: -89, animated: false,
      },
      {
        textureKey: 'deco_arctic_floe_seal_bay',
        positions: [{ x: 330, y: 840 }],
        displaySize: { w: 620, h: 620 }, depth: -89, animated: false,
      },
      {
        textureKey: 'deco_arctic_floe_glacier_garden',
        positions: [{ x: 1870, y: 830 }],
        displaySize: { w: 620, h: 825 }, depth: -89, animated: false,
      },
      {
        textureKey: 'deco_arctic_floe_aurora_crown',
        positions: [{ x: 1100, y: 205 }],
        displaySize: { w: 840, h: 280 }, depth: -89, animated: false,
      },
      {
        textureKey: 'deco_arctic_igloo',
        positions: [{ x: 950, y: 610 }],
        displaySize: { w: 190, h: 150 }, depth: -43, animated: false,
      },
      {
        textureKey: 'deco_arctic_research_tent',
        positions: [{ x: 1260, y: 610, flipX: true }],
        displaySize: { w: 190, h: 145 }, depth: -43, animated: false,
      },
      {
        textureKey: 'deco_arctic_sled',
        positions: [{ x: 930, y: 930, rotationDeg: -8 }],
        displaySize: { w: 145, h: 95 }, depth: -42, animated: false,
      },
      {
        textureKey: 'deco_arctic_snowmobile',
        positions: [{ x: 1310, y: 925, rotationDeg: 7, flipX: true }],
        displaySize: { w: 155, h: 105 }, depth: -42, animated: false,
      },
      {
        textureKey: 'deco_arctic_fishing_hole',
        positions: [{ x: 300, y: 815 }, { x: 470, y: 700, flipX: true }],
        displaySize: { w: 145, h: 105 }, depth: -88, animated: false,
      },
      {
        textureKey: 'deco_arctic_ice_arch',
        positions: [
          { x: 1720, y: 470 },
          { x: 2050, y: 1180, flipX: true },
          { x: 1635, y: 1240 },
        ],
        displaySize: { w: 185, h: 165 }, depth: -47, animated: false,
      },
      {
        textureKey: 'deco_arctic_ice_spires',
        positions: [{ x: 1940, y: 420 }, { x: 1760, y: 880, flipX: true }, { x: 2040, y: 960 }],
        displaySize: { w: 135, h: 145 }, depth: -48, animated: false,
      },
      {
        textureKey: 'deco_arctic_snowy_rock',
        positions: [
          { x: 170, y: 350 }, { x: 520, y: 1240, flipX: true },
          { x: 680, y: 1320 }, { x: 1500, y: 1320, flipX: true },
          { x: 1540, y: 1420 }, { x: 2110, y: 620, flipX: true },
        ],
        displaySize: { w: 105, h: 90 }, depth: -50, animated: false,
      },
      {
        textureKey: 'deco_arctic_supply_stack',
        positions: [{ x: 1160, y: 580 }, { x: 690, y: 1375, flipX: true }],
        displaySize: { w: 110, h: 100 }, depth: -41, animated: false,
      },
      {
        textureKey: 'deco_arctic_fish_rack',
        positions: [{ x: 520, y: 830 }, { x: 210, y: 1050, flipX: true }],
        displaySize: { w: 125, h: 115 }, depth: -44, animated: false,
      },
      {
        textureKey: 'deco_arctic_flag',
        positions: [{ x: 1100, y: 540 }],
        displaySize: { w: 95, h: 135 }, depth: -40,
        motion: { kind: 'float', offsetX: 2, offsetY: 3, rotationDeg: 2, durationMs: 2200 },
      },
      {
        textureKey: 'deco_arctic_ice_crack',
        positions: [
          { x: 660, y: 780, rotationDeg: 18 },
          { x: 1540, y: 790, rotationDeg: -12 },
        ],
        displaySize: { w: 135, h: 90 }, depth: -87, animated: false,
      },
      {
        textureKey: 'deco_arctic_north_pole',
        positions: [{ x: 850, y: 225 }],
        displaySize: { w: 105, h: 150 }, depth: -44, animated: false,
      },
    ],

    eggIds: [
      'egg-arctic-frost',
      'egg-arctic-pack-ice',
      'egg-arctic-aurora',
      'egg-arctic-polar-crown',
    ],

    teleportsTo: [],
    coinMultiplier: 100_000_000,          // documentation-only; rewardy są wpisane per dest
    exclusivePetIds: undefined,           // 65 petów przez origin.biomeId

    enabled: true,                         // ⭐ GO-LIVE 2026-09-04
  },

  // ═════════════════════════════════════════════════════════════════════════
  // ŚWIAT FLAG — Biom EDUKACYJNY, kontynent EUROPA (`educational: true`).
  // Poza drabinką tierów: tier 0 → koszt i wymagana moc = 0 (darmowy wstęp,
  // odblokowany od startu — patrz seed biomesUnlocked). coinMultiplier=1, więc
  // nie skraca progresji competitive. Destructibles reużyte z Łąki (renderują się
  // ze swoim biomeId), tło/flagi mają placeholder do czasu wygenerowania assetów.
  // ═════════════════════════════════════════════════════════════════════════
  {
    id: 'biome-flags-europe',
    name: { pl: 'Flagi Europy', en: 'Flags of Europe' },
    tier: 0,
    educational: true,

    unlock: {
      cost: { currency: 'coins', amount: biomeUnlockCost(0) }, // 0 — darmowy
      // requiredPower / requiredBiomeId — brak (wstęp darmowy od startu)
    },

    worldSize: { w: 2000, h: 1500 },
    playerSpawn: { x: 1000, y: 750 },

    scene: {
      backgroundTextureKey: 'bg_flags_europe_tile',
      backgroundColor: '#A9D6E5',          // pastelowy błękit (mapa/morze) — fallback i kolor pod teksturą
      atmosphere: 'meadow_sun',            // ciepłe, pogodne światło (proceduralne, bez assetów)
    },

    destructibleSpawns: [
      // Reużyte generyczne obiekty z Łąki — kilka stosów monet + skrzynia + kryształ,
      // żeby dziecko szybko uzbierało 30 monet na jajko flag. Renderują się z art Łąki.
      {
        destructibleId: 'dest-coin-pile-s',
        positions: [
          { x: 1200, y: 650 },
          { x:  800, y: 850 },
          { x: 1100, y: 900 },
          { x:  900, y: 600 },
        ],
      },
      {
        destructibleId: 'dest-coin-pile-m',
        positions: [
          { x: 1400, y: 500 },
          { x:  600, y: 700 },
        ],
      },
      {
        destructibleId: 'dest-coin-pile-l',
        positions: [
          { x: 1600, y: 400 },
        ],
      },
      {
        destructibleId: 'dest-chest-wood',
        positions: [
          { x: 500, y: 500 },
        ],
      },
      {
        destructibleId: 'dest-crystal',
        positions: [
          { x: 300, y: 300 },
        ],
      },
    ],

    eggIds: ['egg-flags-europe'],

    teleportsTo: [],
    coinMultiplier: 1.0,                   // jak Łąka — nie psuje ekonomii competitive
    exclusivePetIds: undefined,            // 15 flag (Europa) zdefiniowanych w pets.config.ts

    enabled: true,
  },

  // ═════════════════════════════════════════════════════════════════════════
  // PUSZCZA NIEPOŁOMICKA — biom EDUKACYJNY nr 2 (`educational: true`).
  //
  // Prawdziwy las pod Krakowem: królewskie łowisko Kazimierza Wielkiego, dziś
  // rezerwaty (Lipówka, Długosz Królowej Jadwigi) i Ośrodek Hodowli Żubrów
  // w Niepołomicach (działa od 1939 r.). Stworki = 10 grzybów, 10 roślin/drzew,
  // 20 zwierząt i Żubr (huge).
  //
  // Poza drabinką tierów: tier 0 → koszt i wymagana moc = 0 (darmowy wstęp,
  // odblokowany od startu — patrz seed `biomesUnlocked` w PlayerSave.schema.ts,
  // który liczy `ENABLED_BIOMES.filter(educational)`). `coinMultiplier: 1`,
  // więc biom NIE skraca progresji competitive. Destructibles reużyte z Łąki —
  // renderują się swoim artem, a leśne odpowiedniki to opcjonalny polish.
  //
  // ═════════════════════════════════════════════════════════════════════════
  {
    id: 'biome-puszcza',
    name: { pl: 'Puszcza', en: 'Primeval Forest' },
    tier: 0,
    educational: true,

    unlock: {
      cost: { currency: 'coins', amount: biomeUnlockCost(0) }, // 0 — darmowy
      // requiredPower / requiredBiomeId — brak (wstęp darmowy od startu)
    },

    worldSize: { w: 2200, h: 1600 },
    playerSpawn: { x: 1100, y: 800 },      // polana na środku, ze wszystkich stron las

    scene: {
      backgroundTextureKey: 'bg_puszcza_tile',
      backgroundColor: '#3E6B3A',          // głęboka leśna zieleń — fallback i kolor pod teksturą
      ambientFx: 'forest_leaves',          // opadające liście + pyłki w słońcu
      atmosphere: 'forest_shade',          // chłodny cień koron + ciepłe przebłyski słońca
      critters: 'forest_critters',         // motyl leśny, ptaszek, biedronka na ściółce
    },

    destructibleSpawns: [
      // Reużyte generyczne obiekty z Łąki (jak w biomie flag) — dziecko szybko
      // zbiera monety na Jajko Runa Leśnego. Renderują się z artem Łąki.
      {
        destructibleId: 'dest-coin-pile-s',
        positions: [
          { x: 1350, y:  700 },
          { x:  850, y:  950 },
          { x: 1200, y: 1050 },
          { x:  950, y:  620 },
          { x: 1600, y: 1250 },
        ],
      },
      {
        destructibleId: 'dest-coin-pile-m',
        positions: [
          { x: 1550, y:  520 },
          { x:  620, y:  760 },
          { x: 1400, y: 1400 },
        ],
      },
      {
        destructibleId: 'dest-coin-pile-l',
        positions: [
          { x: 1800, y:  420 },
          { x:  420, y: 1200 },
        ],
      },
      {
        destructibleId: 'dest-chest-wood',
        positions: [
          { x:  520, y:  480 },
          { x: 1900, y:  980 },
        ],
      },
      {
        destructibleId: 'dest-chest-stone',
        positions: [
          { x: 2000, y: 1450 },
        ],
      },
      {
        destructibleId: 'dest-crystal',
        positions: [
          { x: 280, y: 280 },              // daleki róg — wymusza eksplorację puszczy
        ],
      },
    ],

    decorations: [
      // Czysto wizualne (bez kolizji). Gęsto, żeby czuć LAS, nie łąkę z drzewami.
      {
        textureKey: 'deco_puszcza_oak',
        positions: [
          { x:  300, y:  620 }, { x:  700, y:  340 }, { x: 1250, y:  300 },
          { x: 1750, y:  700 }, { x:  480, y: 1420 }, { x: 1950, y: 1300 },
        ],
        scale: 1.6,
        randomFlip: true,
      },
      {
        textureKey: 'deco_puszcza_pine',
        positions: [
          { x:  180, y:  920 }, { x:  900, y: 1380 }, { x: 1520, y:  980 },
          { x: 2050, y:  560 }, { x: 1080, y:  180 }, { x:  640, y: 1100 },
        ],
        scale: 1.5,
        randomFlip: true,
      },
      {
        textureKey: 'deco_puszcza_fern',
        positions: [
          { x:  560, y:  880 }, { x: 1180, y:  560 }, { x: 1680, y: 1120 },
          { x:  820, y: 1220 }, { x: 1420, y:  820 }, { x:  260, y: 1080 },
          { x: 1900, y:  820 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_puszcza_mushroom_cluster',
        positions: [
          { x:  740, y:  700 }, { x: 1320, y: 1200 }, { x: 1780, y:  380 },
          { x:  400, y:  780 }, { x: 1560, y: 1480 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_puszcza_log',
        positions: [
          { x: 1020, y:  980 }, { x:  600, y: 1320 }, { x: 1860, y: 1140 },
          { x: 1240, y:  460 },
        ],
        randomFlip: true,
      },
      {
        textureKey: 'deco_puszcza_anthill',
        positions: [
          { x:  880, y:  520 }, { x: 1640, y:  660 }, { x: 1120, y: 1420 },
        ],
      },
    ],

    eggIds: ['egg-puszcza-runo', 'egg-puszcza-legend'],

    teleportsTo: [],
    coinMultiplier: 1.0,                   // jak Łąka i biom flag — nie psuje ekonomii competitive
    exclusivePetIds: undefined,            // 41 petów wiązanych przez origin.biomeId

    enabled: true,                         // ⭐ GO-LIVE 2026-09-16 (komplet grafik zweryfikowany)
  },

  // ═════════════════════════════════════════════════════════════════════════
  // EVENT „WAKACJE CAPIBAR" — Plaża Capibar (EV-1b)
  // Biom EVENTOWY: `enabled:false` (NIE trafia do ENABLED_BIOMES / zwykłej mapy),
  // widoczność + preload napędza `getActiveEvents()` (kafel/ładowanie tylko w
  // oknie eventu). Assety w `assets/events/capibara_summer/` (eventAssetFolder).
  // Wejście DARMOWE (cost 0, bez wymogu mocy) — event dla każdego, też słabego
  // konta. Realne wejście wymaga serwerowego SERVER_BIOMES (EV-1b-2/EV-5);
  // lokalnie testowane dev-teleportem. Muszelki naliczane serwerowo (EV-1b-2).
  // ═════════════════════════════════════════════════════════════════════════
  {
    id: 'biome-event-capibara-beach',
    name: { pl: 'Plaża Capibar', en: 'Capybara Beach' },
    tier: 0,
    eventAssetFolder: 'capibara_summer',

    unlock: {
      cost: { currency: 'coins', amount: 0 }, // darmowy — event dla każdego
    },

    worldSize: { w: 2200, h: 1500 },
    playerSpawn: { x: 1100, y: 1060 },        // środek-dół, na piasku (morze u góry)
    movement: {
      blockedWater: {
        shoreY: 390,
        walkways: [{ xMin: 1020, xMax: 1180, yMin: 205, yMax: 390 }],
        splashTextureKey: 'deco_beach_water_splash',
        splashCooldownMs: 520,
      },
    },

    scene: {
      backgroundTextureKey: 'bg_capibara_beach_tile',
      backgroundColor: '#F4D9A0',             // złoty piasek — fallback pod teksturą
      atmosphere: 'beach_sun',                // ciepłe wakacyjne słońce (proceduralne)
      // parallax chmur ZDJĘTY: w widoku z góry chmury kładły się na piasku (SCREEN,
      // duże białe kłaczki po całym polu). Top-down plaża nie potrzebuje nieba.
      critters: 'beach_critters',             // mewy nad morzem + kraby na piasku
    },

    destructibleSpawns: [
      {
        destructibleId: 'dest-beach-sandcastle',
        positions: [
          { x:  760, y: 1375 }, { x:  980, y: 1300 }, { x: 1500, y: 1180 },
          { x: 1820, y: 1320 }, { x:  720, y:  900 },
        ],
      },
      {
        destructibleId: 'dest-beach-bucket',
        positions: [
          { x:  360, y: 1320 }, { x: 1240, y: 1360 }, { x: 1680, y: 1000 },
          { x:  900, y: 1100 },
        ],
      },
      {
        destructibleId: 'dest-beach-shellpile',
        positions: [
          { x:  600, y:  680 }, { x: 1420, y:  760 }, { x: 1950, y:  760 },
        ],
      },
      {
        destructibleId: 'dest-beach-chest',
        positions: [
          { x:  340, y:  900 }, { x: 1980, y: 1120 },
        ],
      },
      {
        destructibleId: 'dest-beach-treasure',
        positions: [
          { x: 1100, y:  560 },                // na/przy pomoście, bliżej wody
        ],
      },
      {
        destructibleId: 'dest-beach-giant-shell',
        positions: [
          { x: 2000, y:  440 },                // daleki róg — wymusza eksplorację
        ],
      },
    ],

    decorations: [
      // Pas morza/brzegu — jeden TileSprite powtarzany w poziomie na pełną
      // szerokość świata. Ładujemy źródłowy PNG 512×512, bo zwykłe dekoracje
      // są celowo skalowane do 64×64 w WorldScene. Wysokość 704 + tileScaleY
      // rozciąga pojedynczy kafel w pionie, zamiast repeatować drugi brzeg/falę.
      {
        textureKey: 'deco_beach_shoreline',
        assetFormat: 'png',
        tileStrip: { y: 0, height: 704, tileScaleY: 1.375, depth: -90 },
      },
      // Duże statyczne landmarki eventowe — bez sway/breath; mają budować
      // poczucie wakacyjnego eventu, a nie zachowywać się jak małe roślinki.
      {
        textureKey: 'deco_beach_pier_topdown',
        positions: [{ x: 1100, y: 430 }],
        displaySize: { w: 360, h: 540 },
        depth: -58,
        animated: false,
      },
      // Asset interaktywnego połowu z mola ładowany przez preload; WorldScene
      // tworzy go ręcznie z hitboxem i markerem dostępności.
      { textureKey: 'deco_beach_pier_fishing_spot', animated: false },
      // FX asset dla feedbacku przy próbie wejścia do morza; renderowany ręcznie.
      { textureKey: 'deco_beach_water_splash', animated: false },
      {
        textureKey: 'deco_beach_water_buoys',
        positions: [{ x: 720, y: 315 }, { x: 1640, y: 300 }],
        displaySize: { w: 190, h: 49 },
        depth: -57,
        alpha: 0.96,
        animated: false,
        motion: { kind: 'float', offsetX: 12, offsetY: 8, rotationDeg: 2.5, durationMs: 3400 },
      },
      {
        textureKey: 'deco_beach_drift_crate',
        positions: [{ x: 1510, y: 300 }],
        displaySize: { w: 122, h: 121 },
        depth: -57,
        animated: false,
        motion: { kind: 'float', offsetX: 10, offsetY: 10, rotationDeg: 3, durationMs: 3800, delayMs: 350 },
      },
      {
        textureKey: 'deco_beach_fish_shadows',
        positions: [{ x: 835, y: 250 }, { x: 1860, y: 235 }],
        displaySize: { w: 210, h: 139 },
        depth: -88,
        alpha: 0.58,
        animated: false,
        motion: { kind: 'float', offsetX: 18, offsetY: 7, rotationDeg: 1.5, durationMs: 4600 },
      },
      {
        textureKey: 'deco_beach_gate_ground_shadow',
        positions: [{ x: 1100, y: 1125 }],
        displaySize: { w: 660, h: 330 },
        depth: -59,
        animated: false,
      },
      {
        textureKey: 'deco_beach_event_shell_gate',
        positions: [{ x: 1100, y: 1045 }],
        displaySize: { w: 660, h: 440 },
        // Foreground świata: celowo nad graczem/petami i ich efektami, ale pod
        // popupami damage/nagród/UI. Daje efekt przechodzenia pod łukiem bramy.
        depth: 35,
        animated: false,
      },
      {
        textureKey: 'deco_beach_event_trader_counter',
        positions: [{ x: 410, y: 1115 }],
        displaySize: { w: 460, h: 266 },
        depth: -54,
        animated: false,
      },
      {
        textureKey: 'deco_beach_capybara_sand_statue',
        positions: [{ x: 1780, y: 1040 }],
        displaySize: { w: 320, h: 213 },
        depth: -52,
        animated: false,
      },
      {
        textureKey: 'deco_beach_capybara_float',
        positions: [{ x: 425, y: 245 }],
        displaySize: { w: 315, h: 210 },
        depth: -58,
        animated: false,
        motion: { kind: 'float', offsetX: 95, offsetY: 10, rotationDeg: 2, durationMs: 12000, delayMs: 500 },
      },
      { textureKey: 'deco_beach_palm', displaySize: { w: 104, h: 104 }, depth: -53, positions: [
          { x: 260, y: 1360 }, { x: 1920, y: 1240 }, { x: 1150, y: 1400 },
        ], randomFlip: true },
      { textureKey: 'deco_beach_umbrella', positions: [
          { x: 640, y: 1080 }, { x: 1580, y: 1120 },
        ], randomFlip: true },
      { textureKey: 'deco_beach_lounger', positions: [
          { x: 780, y: 1180 }, { x: 1480, y: 1040 },
        ], randomFlip: true },
      { textureKey: 'deco_beach_ring', positions: [{ x: 880, y: 1280 }], randomFlip: true },
      { textureKey: 'deco_beach_ball', positions: [{ x: 1320, y: 1280 }] },
      { textureKey: 'deco_beach_shells', positions: [
          { x: 460, y: 1420 }, { x: 1760, y: 1420 },
        ], randomFlip: true },
    ],

    // Jajko eventowe + pety = EV-2. Pusto, żeby sklep nie szukał nieistniejącego defu.
    eggIds: [],

    teleportsTo: [],
    coinMultiplier: 1.0,                       // documentation-only (jak inne biomy)

    // Master-switch biomu: false → poza ENABLED_BIOMES. Widoczność biomu eventowego
    // steruje getActiveEvents() (przez biomeId eventu), nie to pole. Zostaje false.
    enabled: false,
  },

  // Przyszłe biomy (zone 5+) będą dopisywane tutaj.
  // Wzór kosztu i wymaganej mocy z balance.config.ts:
  //   tier 2: cost biomeUnlockCost(2) = 50_000, power biomePowerRequirement(2) = 200
  //   tier 3: cost biomeUnlockCost(3) = 750_000, power biomePowerRequirement(3) = 2_000
  //   tier 4: cost biomeUnlockCost(4) = 11_250_000, power biomePowerRequirement(4) = 20_000
  //   tier 5: cost biomeUnlockCost(5) = 168_750_000, power biomePowerRequirement(5) = 200_000
];

void biomePowerRequirement; // (re-export reservation — funkcja używana przy dodawaniu kolejnych biomów)

/** Mapa pomocnicza id → BiomeDef. */
export const BIOMES_BY_ID: Record<string, BiomeDef> = Object.fromEntries(
  BIOMES.map((b) => [b.id, b]),
);

/** Lista wszystkich włączonych biomów (do listy wyboru w UI / teleportach). */
export const ENABLED_BIOMES: BiomeDef[] = BIOMES.filter((b) => b.enabled);
